// frontend/src/components/announcements/CreateAnnouncementForm.jsx
// Form for creating new announcements with rich text editor

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import RichTextEditor from "../common/RichTextEditor";
import RichTextDisplay from "../common/RichTextDisplay";
import "../../assets/css/rich-text-editor.css";

const CreateAnnouncementForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    priority: "normal",
    announcement_type: "general",
    version: "",
    start_date: new Date().toISOString().slice(0, 16),
    end_date: "",
  });
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Handle rich text content change
  const handleContentChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      content: content,
    }));
    if (errors.content) {
      setErrors((prev) => ({ ...prev, content: null }));
    }
  };

  // Check if content is empty (accounting for empty HTML)
  const isContentEmpty = (content) => {
    if (!content) return true;
    const stripped = content.replace(/<[^>]*>/g, "").trim();
    return stripped === "";
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length > 255) {
      newErrors.title = "Title must be less than 255 characters";
    }

    if (isContentEmpty(formData.content)) {
      newErrors.content = "Content is required";
    }

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (formData.end_date && formData.start_date) {
      if (new Date(formData.end_date) <= new Date(formData.start_date)) {
        newErrors.end_date = "End date must be after start date";
      }
    }

    // Validate version format if provided
    if (formData.version) {
      const dateFormat = /^\d{4}\.\d{2}\.\d{2}$/;
      const semverFormat = /^\d+\.\d+\.\d+$/;
      if (!dateFormat.test(formData.version) && !semverFormat.test(formData.version)) {
        newErrors.version = "Invalid format. Use YYYY.MM.DD or X.Y.Z";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await secureAxios.post("/api/announcements", {
        title: formData.title.trim(),
        content: formData.content,
        priority: formData.priority,
        announcement_type: formData.announcement_type,
        version: formData.version.trim() || null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date
          ? new Date(formData.end_date).toISOString()
          : null,
      });

      toast.success("Announcement created successfully!");
      navigate("/crm/announcements");
    } catch (err) {
      console.error("Error creating announcement:", err);
      toast.error(err.response?.data?.error || "Failed to create announcement");
    } finally {
      setLoading(false);
    }
  };

  // Priority options
  const priorities = [
    {
      value: "low",
      label: "Low",
      icon: "bi-bell",
      description: "General information",
    },
    {
      value: "normal",
      label: "Normal",
      icon: "bi-info-circle",
      description: "Standard announcement",
    },
    {
      value: "high",
      label: "High",
      icon: "bi-exclamation-circle",
      description: "Important update",
    },
    {
      value: "urgent",
      label: "Urgent",
      icon: "bi-exclamation-triangle",
      description: "Critical - requires immediate attention",
    },
  ];

  // Announcement type options
  const announcementTypes = [
    { value: "general", label: "General", icon: "bi-megaphone" },
    { value: "release_notes", label: "Release Notes", icon: "bi-journal-text" },
    { value: "feature", label: "New Feature", icon: "bi-star" },
    { value: "bugfix", label: "Bug Fix", icon: "bi-bug" },
    { value: "maintenance", label: "Maintenance", icon: "bi-tools" },
  ];

  return (
    <div style={{ minWidth: 0, maxWidth: "100%" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2>
            <i className="bi bi-plus-circle me-2"></i>
            Create Announcement
          </h2>
          <p className="text-muted mb-0">
            Create a new announcement for all tenants
          </p>
        </div>
        <Link to="/crm/announcements" className="btn btn-outline-secondary">
          <i className="bi bi-arrow-left me-1"></i>
          Back to List
        </Link>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="announcement-form">
            {/* Title */}
            <div className="mb-3">
              <label htmlFor="title" className="form-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter announcement title"
                maxLength={255}
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
              <small className="text-muted">
                {formData.title.length}/255 characters
              </small>
            </div>

            {/* Rich Text Content */}
            <div className="mb-3">
              <label className="form-label">
                Content <span className="text-danger">*</span>
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={handleContentChange}
                placeholder="Enter announcement content. You can paste screenshots directly (Ctrl+V)!"
                minHeight="250px"
              />
              {errors.content && (
                <div className="text-danger small mt-1">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {errors.content}
                </div>
              )}
            </div>

            {/* Type and Version Row */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="announcement_type" className="form-label">
                  Announcement Type
                </label>
                <select
                  className="form-select"
                  id="announcement_type"
                  name="announcement_type"
                  value={formData.announcement_type}
                  onChange={handleChange}
                >
                  {announcementTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  Categorize this announcement
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="version" className="form-label">
                  Version <span className="text-muted">(optional)</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.version ? "is-invalid" : ""}`}
                  id="version"
                  name="version"
                  value={formData.version}
                  onChange={handleChange}
                  placeholder="e.g., 2026.01.30"
                />
                {errors.version && (
                  <div className="invalid-feedback">{errors.version}</div>
                )}
                <small className="text-muted">
                  Link to a specific release version
                </small>
              </div>
            </div>

            {/* Priority */}
            <div className="mb-4">
              <label className="form-label">
                Priority <span className="text-danger">*</span>
              </label>
              <div className="priority-selector">
                {priorities.map((priority) => (
                  <div
                    key={priority.value}
                    className={`priority-option ${priority.value}`}
                  >
                    <input
                      type="radio"
                      id={`priority-${priority.value}`}
                      name="priority"
                      value={priority.value}
                      checked={formData.priority === priority.value}
                      onChange={handleChange}
                    />
                    <label htmlFor={`priority-${priority.value}`}>
                      <i className={`bi ${priority.icon} d-block mb-1`}></i>
                      <strong>{priority.label}</strong>
                      <small className="d-block text-muted">
                        {priority.description}
                      </small>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="start_date" className="form-label">
                  Start Date <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${
                    errors.start_date ? "is-invalid" : ""
                  }`}
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                />
                {errors.start_date && (
                  <div className="invalid-feedback">{errors.start_date}</div>
                )}
                <small className="text-muted">
                  When the announcement becomes visible
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="end_date" className="form-label">
                  End Date <span className="text-muted">(optional)</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${
                    errors.end_date ? "is-invalid" : ""
                  }`}
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  min={formData.start_date}
                />
                {errors.end_date && (
                  <div className="invalid-feedback">{errors.end_date}</div>
                )}
                <small className="text-muted">
                  Leave empty for no expiration
                </small>
              </div>
            </div>

            {/* Preview */}
            {(formData.title || !isContentEmpty(formData.content)) && (
              <div className="mb-4">
                <label className="form-label">Preview</label>
                <div
                  className="card"
                  style={{
                    borderLeft: `4px solid ${
                      formData.priority === "urgent"
                        ? "#dc3545"
                        : formData.priority === "high"
                        ? "#ffc107"
                        : formData.priority === "normal"
                        ? "#0dcaf0"
                        : "#6c757d"
                    }`,
                  }}
                >
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5
                      className="mb-0"
                      style={{
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {formData.title || "Untitled"}
                    </h5>
                    <div className="d-flex gap-2">
                      {formData.announcement_type && formData.announcement_type !== "general" && (
                        <span className="badge bg-secondary">
                          {announcementTypes.find(t => t.value === formData.announcement_type)?.label}
                        </span>
                      )}
                      {formData.version && (
                        <span className="badge bg-primary">v{formData.version}</span>
                      )}
                    </div>
                  </div>
                  <div className="card-body">
                    <RichTextDisplay content={formData.content} />
                  </div>
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="d-flex justify-content-end gap-2">
              <Link to="/crm/announcements" className="btn btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i>
                    Create Announcement
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateAnnouncementForm;